// HTMLAudio（BGM・効果音）を Web Audio グラフへ載せ替えて音量制御するための配管。
//
// iOS Safari/WebKit は HTMLMediaElement.volume への代入を無視する（音量は
// ハードウェアボタンのみ・ミュートも効かない）。そこで各 HTMLAudio を
// `createMediaElementSource → GainNode → destination` で繋ぎ、GainNode.gain で
// 音量・ミュートを効かせる（GainNode は iOS でも効く）。全音源は同一オリジン
// （/sounds/...）なので CORS による無音化は起きない。
//
// createMediaElementSource は要素あたり 1 回のみ。生成した GainNode は要素ごとに
// WeakMap で保持し、要素が破棄されれば一緒に GC される。Web Audio に繋げない環境
// （非対応・テスト・例外）では .volume へフォールバックして無音化を防ぐ。

import { getSharedAudioContext } from './behaviors/audio'

/** 1 要素ぶんの Web Audio ノード（破棄時に切断するため source も保持する）。 */
interface RoutedNodes {
  source: MediaElementAudioSourceNode
  gain: GainNode
}

/** 要素 → 結線済みノード。要素破棄で GC されるが、明示切断は releaseHtmlAudio で行う。 */
const nodesByElement = new WeakMap<HTMLMediaElement, RoutedNodes>()

/** Web Audio 接続を試行済みの要素（失敗時の再試行ループを防ぐ）。 */
const attempted = new WeakSet<HTMLMediaElement>()

/**
 * 要素を Web Audio グラフへ繋ぎ、音量制御用 GainNode を返す（繋げないとき null）。
 * 既に繋いだ要素はその GainNode を再利用する。
 */
const routeElement = (audio: HTMLMediaElement): GainNode | null => {
  const existing = nodesByElement.get(audio)
  if (existing) {
    return existing.gain
  }
  if (attempted.has(audio)) {
    return null
  }
  attempted.add(audio)
  try {
    const context = getSharedAudioContext()
    const source = context.createMediaElementSource(audio)
    const gain = context.createGain()
    source.connect(gain)
    gain.connect(context.destination)
    nodesByElement.set(audio, { source, gain })
    return gain
  } catch (error) {
    console.warn('[preview] HTMLAudio の Web Audio 接続に失敗しました', error)
    return null
  }
}

/**
 * HTMLAudio の音量を設定する（0..1）。Web Audio 経路（GainNode）で効かせ、iOS の
 * volume 無視を回避する。繋げない環境では要素の .volume へフォールバックする。
 */
export const setHtmlAudioVolume = (
  audio: HTMLMediaElement,
  volume: number,
): void => {
  const clamped = Math.min(1, Math.max(0, volume))
  const gain = routeElement(audio)
  if (gain) {
    gain.gain.value = clamped
    // gain で制御するため要素の volume は最大固定にする（Web Audio 経路では
    // .volume が二重に掛かるのを避ける。iOS では元々 .volume は無視される）。
    audio.volume = 1
  } else {
    audio.volume = clamped
  }
}

/**
 * 要素を破棄するとき、Web Audio グラフから切り離す。
 * MediaElementSource は要素を、各ノードは destination への接続を保持し続けるため、
 * 切断しないとシーン変更・BGM 差し替えのたびに旧ノードと音声要素がセッション中
 * 残り続ける。生成元（PreviewPanel）が要素を pause して捨てる箇所で必ず呼ぶ。
 */
export const releaseHtmlAudio = (audio: HTMLMediaElement): void => {
  const nodes = nodesByElement.get(audio)
  if (!nodes) {
    return
  }
  try {
    nodes.source.disconnect()
    nodes.gain.disconnect()
  } catch (error) {
    console.warn('[preview] HTMLAudio のノード切断に失敗しました', error)
  }
  nodesByElement.delete(audio)
}
